import React, {useState} from 'react';

import {Button, Icon, Inert, Input, Label, SingleSearchAndSelect, Text} from '@albert-io/atomic';
import classNames from 'classnames';
import {fromJS} from 'immutable';

import {stateMap} from 'client/AccountSettings/ContentPersonalization/ContentPersonalization.util';

import {COUNTRY_OPTIONS, USA} from './schoolAssociation.utils';

import type {CreatedHomeschool} from './schoolAssociation.types';

const COUNTRY_OPTION_LIST = fromJS(COUNTRY_OPTIONS);
const STATE_OPTION_LIST = fromJS(
  Object.entries(stateMap).map(([abbr, stateName]) => ({
    label: `${stateName} (${abbr})`,
    value: abbr
  }))
);

const optionContent = (option: any) => option.get('label') ?? '';
const optionSearchFilter = (searchString: string) => (option: any) => {
  if (!searchString) return true;
  return (option.get('label') ?? '').toLowerCase().includes(searchString.toLowerCase());
};

interface CreateHomeschoolFormProps {
  onSwitchToSearch: () => void;
  onCreated: (school: CreatedHomeschool) => void;
  initialData?: CreatedHomeschool | null;
  /** Used on fresh form (no initialData) to pre-fill school name; editable. */
  defaultName?: string;
}

export default function CreateHomeschoolForm({
  onSwitchToSearch,
  onCreated,
  initialData,
  defaultName = ''
}: CreateHomeschoolFormProps) {
  const [name, setName] = useState(initialData?.name ?? defaultName);
  const [country, setCountry] = useState(initialData?.country ?? USA);
  const [city, setCity] = useState(initialData?.city ?? '');
  const [state, setState] = useState(initialData?.state ?? '');

  const isUsa = country === USA;
  // name/city/country always required. state required only for USA.
  const isValid = Boolean(name.trim() && city.trim() && country && (!isUsa || state));

  const handleCountryChange = (next: string) => {
    setCountry(next);
    if (next !== USA) setState('');
  };

  const selectedCountry = COUNTRY_OPTION_LIST.find((opt: any) => opt.get('value') === country);
  const selectedState = STATE_OPTION_LIST.find((opt: any) => opt.get('value') === state);

  return (
    <div className='flex flex-col gap-x5'>
      <div className='flex flex-col gap-x1-half'>
        <div>
          <Label>School Name</Label>
          <Input
            className='mt-x1'
            type='text'
            placeholder="e.g. Albert's High School"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          />
        </div>
        <div>
          <Label>Country</Label>
          <SingleSearchAndSelect
            className='mt-x1'
            inputType='text'
            data={COUNTRY_OPTION_LIST}
            defaultValue={selectedCountry}
            content={optionContent}
            searchFilter={optionSearchFilter}
            onSelect={(item: any) => handleCountryChange(item?.get('value') ?? '')}
            placeholder='Select a Country'
            openOnFocus
          />
        </div>
        <div className='flex gap-x1-half'>
          <div className='flex-1'>
            <Label>City</Label>
            <Input
              className='mt-x1-half'
              type='text'
              placeholder='e.g. Chicago'
              value={city}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCity(e.target.value)}
            />
          </div>
          <div className={classNames('flex-1', {'opacity-60': !isUsa})}>
            <Label>State</Label>
            <Inert disabled={isUsa}>
              <SingleSearchAndSelect
                key={country}
                className='mt-x1'
                inputType='text'
                data={STATE_OPTION_LIST}
                defaultValue={selectedState}
                content={optionContent}
                searchFilter={optionSearchFilter}
                onSelect={(item: any) => setState(item?.get('value') ?? '')}
                placeholder='Select a State'
                openOnFocus
              />
            </Inert>
          </div>
        </div>
      </div>
      <div className='flex gap-x1-half'>
        <Button
          color='secondary'
          variant='faded'
          roundness='pill'
          className='flex-1 gap-x1-quarter'
          onClick={onSwitchToSearch}
        >
          <Icon icon='arrow-left' />
          <Text color='secondary'>Search Instead</Text>
        </Button>
        <Button
          color='primary'
          roundness='pill'
          className='flex-1 gap-x1-quarter'
          disabled={!isValid}
          onClick={() => onCreated({name: name.trim(), country, city: city.trim(), state})}
        >
          <Text color='primary-inverse-static'>Create Homeschool</Text>
        </Button>
      </div>
    </div>
  );
}
